import * as THREE from 'three'

function getImageDimensions(image: TexImageSource): readonly [number, number] | undefined {
  if ('naturalWidth' in image && 'naturalHeight' in image) {
    return [image.naturalWidth, image.naturalHeight]
  }
  if ('videoWidth' in image && 'videoHeight' in image) {
    return [image.videoWidth, image.videoHeight]
  }
  if ('displayWidth' in image && 'displayHeight' in image) {
    return [image.displayWidth, image.displayHeight]
  }
  if ('width' in image && 'height' in image) return [image.width, image.height]
  return undefined
}

function usesMipmaps(filter: THREE.MinificationTextureFilter): boolean {
  return (
    filter === THREE.NearestMipmapNearestFilter ||
    filter === THREE.NearestMipmapLinearFilter ||
    filter === THREE.LinearMipmapNearestFilter ||
    filter === THREE.LinearMipmapLinearFilter
  )
}

/** 按 Three.js 默认 RGBA8 上传方式估算单个普通二维纹理的 GPU 字节。 */
export function estimateGltfTextureBytes(
  image: TexImageSource,
  minFilter: THREE.MinificationTextureFilter,
  generateMipmaps = true
): number {
  const dimensions = getImageDimensions(image)
  if (!dimensions) return 0
  const [width, height] = dimensions
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return 0
  const baseBytes = width * height * 4
  return generateMipmaps && usesMipmaps(minFilter) ? Math.ceil((baseBytes * 4) / 3) : baseBytes
}

/** 估算已创建 Three.js Texture 的 GPU 字节。 */
export function estimateThreeTextureBytes(texture: THREE.Texture): number {
  // KTX2 的压缩目标和 RGBA8 回退均携带实际 mip 数据，不能再乘 4/3。
  if ((texture as THREE.CompressedTexture).isCompressedTexture) {
    return texture.mipmaps.reduce((sum, mip) => sum + ('data' in mip ? mip.data.byteLength : 0), 0)
  }
  if ((texture as THREE.DataTexture).isDataTexture) {
    if (texture.mipmaps.length > 0)
      return texture.mipmaps.reduce(
        (sum, mip) => sum + ('data' in mip ? mip.data.byteLength : 0),
        0
      )
    const { data, width, height } = (texture as THREE.DataTexture).image
    const baseBytes = data?.byteLength ?? 0
    if (
      !texture.generateMipmaps ||
      !Number.isFinite(width) ||
      !Number.isFinite(height) ||
      width < 1 ||
      height < 1
    )
      return baseBytes
    // WebGL 会为没有显式 mip 的 DataTexture 生成完整链，包含非方形纹理。
    const bytesPerPixel = baseBytes / (width * height)
    let bytes = baseBytes
    let mipWidth = width
    let mipHeight = height
    while (mipWidth > 1 || mipHeight > 1) {
      mipWidth = Math.max(1, Math.floor(mipWidth / 2))
      mipHeight = Math.max(1, Math.floor(mipHeight / 2))
      bytes += mipWidth * mipHeight * bytesPerPixel
    }
    return bytes
  }
  const image = texture.image as TexImageSource | undefined
  if (!image) return 0
  return estimateGltfTextureBytes(image, texture.minFilter, texture.generateMipmaps)
}
